import type { ReactNode } from 'react'
import { NumberIcon } from './NumberIcon'
import { GROUPED_STATIC_TEXT_ROLE } from './NameValueTableRow'
import { isIOS } from '../../utils/utils'

interface NumberedItemProps {
  number: number
  title: string
  subtitle?: string
  children: ReactNode
  borderBottom?: boolean
  /** Render as list item for list semantics (Know Why score pointers). */
  as?: 'div' | 'li'
  /** Combined accessible name for the score row (label + score + rating + description). */
  accessibilityLabel?: string
}

export const NumberedItem = ({
  number,
  title,
  subtitle,
  children,
  borderBottom = true,
  as = 'div',
  accessibilityLabel
}: NumberedItemProps) => {
  const groupLabel = accessibilityLabel ?? [title, subtitle].filter(Boolean).join(', ')
  const contentClassName = `flex flex-row gap-3 items-start px-5 py-6 ${borderBottom ? 'border-b border-[#EBF2F2]' : ''}`

  const visualContent = (
    <div className='flex flex-row gap-3 items-start w-full'>
      <div className='flex items-center justify-center'>
        <NumberIcon number={number} />
      </div>
      <div className='flex flex-col flex-1'>
        <div className='text-[14px] font-[500] leading-[130%] text-[#232323]'>{title}</div>
        {subtitle && <div className='text-sm pt-[2px] font-[400] leading-[130%] text-[#232323]'>{subtitle}</div>}
        {children && <div className='pt-4'>{children}</div>}
      </div>
    </div>
  )

  // iOS VoiceOver: label on listitem. Android TalkBack: role=text on inner node.
  // Do not add list position to the label — <ul>/<li> markup supplies "N of M" once.
  const useListItemLabel = as === 'li' && isIOS()

  if (as === 'li') {
    return (
      <li aria-label={useListItemLabel ? groupLabel : undefined} className='list-none m-0 p-0'>
        <div
          role={useListItemLabel ? undefined : GROUPED_STATIC_TEXT_ROLE}
          aria-label={useListItemLabel ? undefined : groupLabel}
          className={contentClassName}
        >
          <div aria-hidden='true'>{visualContent}</div>
        </div>
      </li>
    )
  }

  return (
    <div className='m-0 p-0'>
      <div role={GROUPED_STATIC_TEXT_ROLE} aria-label={groupLabel} className={contentClassName}>
        <div aria-hidden='true'>{visualContent}</div>
      </div>
    </div>
  )
}
